import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Turnstile from 'react-turnstile';
import { ArrowIcon, CheckIcon, PhoneLink, SERVICES, Seo, SiteHeader } from '../components/shared';

const TURNSTILE_SITE_KEY = '0x4AAAAAABBP2jBbw7gCcGUI';
// cPanel's PHP drops uploads over upload_max_filesize (2 MB by default) without
// failing the request, so the email arrives with no photo. Photos are shrunk
// in the browser to stay under that.
const MAX_PHOTO_BYTES = 1.8 * 1024 * 1024;
const PHOTO_MAX_EDGE = 2000;

// Returns a JPEG no larger than PHOTO_MAX_EDGE on its long side, or the
// original file if it is already small or the browser can't decode it (e.g. HEIC).
async function shrinkPhoto(file) {
  if (file.size <= MAX_PHOTO_BYTES || !file.type.startsWith('image/')) return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }
  const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const name = file.name.replace(/\.[^.]*$/, '') + '.jpg';
  for (const quality of [0.85, 0.7, 0.55]) {
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (blob && blob.size <= MAX_PHOTO_BYTES) return new File([blob], name, { type: 'image/jpeg' });
  }
  return file;
}

const MODES = {
  'one-time': {
    heading: 'Book a free quote',
    endpoint: 'https://studentforhire.ca/SendMail.php',
    services: SERVICES,
  },
  seasonal: {
    heading: 'Book a seasonal package',
    endpoint: 'https://studentforhire.ca/SendMailSeasonal.php',
    services: SERVICES.filter((s) => s.key === 'mow' || s.key === 'weed'),
  },
};

const STEPS = [
  { label: 'Services', question: 'Which services do you need?' },
  { label: 'Contact', question: 'How can we reach you?' },
  { label: 'Property', question: 'Where is the property?' },
  { label: 'Source', question: 'How did you find out about us?' },
  { label: 'Details', question: 'Anything else we should know?' },
];

// Values are unchanged from the old form so referral reporting stays consistent.
const REFERRAL_OPTIONS = [
  { value: 'facebook', label: 'Facebook Ad' },
  { value: 'nextdoor', label: 'Nextdoor' },
  { value: 'referral', label: 'Referral / From a Friend' },
  { value: 'google', label: 'Google' },
  { value: 'social-media', label: 'Social Media' },
  { value: 'flyer', label: 'Flyer in the Mail' },
  { value: 'other', label: 'Other' },
];

const LAWN_AREA_OPTIONS = [
  { value: 'both', label: 'Both front and back lawns' },
  { value: 'one', label: 'Only one lawn (say which in the comments)' },
];
const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];
const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-Weekly' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POSTAL_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  address: '',
  postalCode: '',
  referral: '',
  referrerName: '',
  message: '',
  photo: null,
  lawnArea: '',
  bagClippings: '',
  provideBags: '',
  frequency: '',
  turnstileToken: '',
};

export default function QuoteWizard({ mode = 'one-time' }) {
  const config = MODES[mode];
  const seasonal = mode === 'seasonal';

  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [photoError, setPhotoError] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const questionRef = useRef(null);
  const firstRender = useRef(true);

  // Move focus to the new question so keyboard and screen reader users follow along.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    questionRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [step]);

  const wantsMowing = services.includes('mow');
  const needsBagAnswer = wantsMowing && formData.bagClippings === 'yes';
  const phoneDigits = formData.phone.replace(/\D/g, '');

  const valid = [
    services.length > 0,
    formData.fullName.trim().length > 1 && EMAIL_RE.test(formData.email.trim()) && phoneDigits.length >= 10,
    formData.address.trim().length > 4 && POSTAL_RE.test(formData.postalCode.trim()),
    !!formData.referral && (formData.referral !== 'referral' || formData.referrerName.trim().length > 0),
    (!seasonal || !!formData.frequency)
      && (!wantsMowing || (!!formData.lawnArea && !!formData.bagClippings && (!needsBagAnswer || !!formData.provideBags))),
  ];

  const hints = [
    'Pick at least one service.',
    'Add your name, a valid email and a 10-digit phone number.',
    'Add the address and a postal code like M4B 1B3.',
    formData.referral === 'referral' ? 'Tell us who referred you.' : 'Pick one option.',
    'Answer the questions above.',
  ];

  const isLastStep = step === STEPS.length - 1;
  const stepValid = valid[step];

  const setField = (name, value) => setFormData((prev) => ({ ...prev, [name]: value }));
  const handleInput = (event) => setField(event.target.name, event.target.value);

  const handlePhoto = async (event) => {
    const input = event.target;
    const picked = input.files[0] || null;
    setPhotoError('');
    if (!picked) {
      setField('photo', null);
      return;
    }
    setPhotoBusy(true);
    const file = await shrinkPhoto(picked);
    setPhotoBusy(false);
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError("That photo is too large to send. Please choose a different one, or a screenshot of it.");
      input.value = '';
      setField('photo', null);
      return;
    }
    setField('photo', file);
  };

  const toggleService = (key) => {
    setServices((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleTurnstile = (token) => {
    setFormData((prevData) => ({ ...prevData, turnstileToken: token }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setResponseMessage('');

    if (!formData.turnstileToken) {
      setResponseMessage('Please complete the CAPTCHA verification.');
      setLoading(false);
      return;
    }

    // Field names match what SendMail.php / SendMailSeasonal.php already read.
    const fields = {
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      address: formData.address.trim(),
      postalCode: formData.postalCode.trim().toUpperCase(),
      serviceType: config.services.filter((s) => services.includes(s.key)).map((s) => s.slug).join(', '),
      lawnArea: wantsMowing ? formData.lawnArea : '',
      bagClippings: wantsMowing ? formData.bagClippings : '',
      provideBags: needsBagAnswer ? formData.provideBags : '',
      referral: formData.referral,
      referrerName: formData.referral === 'referral' ? formData.referrerName.trim() : '',
      message: formData.message,
      turnstileToken: formData.turnstileToken,
    };
    if (seasonal) fields.frequency = formData.frequency;

    const formDataToSend = new FormData();
    Object.entries(fields).forEach(([key, value]) => formDataToSend.append(key, value));
    if (formData.photo) {
      formDataToSend.append('photo', formData.photo);
    }

    try {
      const response = await fetch(config.endpoint, {
        method: 'POST',
        body: formDataToSend,
      });

      const result = await response.json();
      if (result.success !== false) {
        setSubmitted(true);
        window.scrollTo({ top: 0 });
      } else {
        setResponseMessage(result.message);
        // Turnstile tokens are single-use: get a fresh one for the retry.
        setField('turnstileToken', '');
        setTurnstileKey((k) => k + 1);
      }
    } catch (error) {
      // Same as the old forms: an unreadable response is treated as sent,
      // because the mail usually went out even when the JSON did not parse.
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    }
    setLoading(false);
  };

  const goNext = () => {
    if (!stepValid || loading || photoBusy) return;
    if (isLastStep) handleSubmit();
    else setStep(step + 1);
  };

  const goBack = () => {
    setResponseMessage('');
    setStep(Math.max(0, step - 1));
  };

  const firstName = formData.fullName.trim().split(/\s+/)[0] || 'there';
  const referralLabel = REFERRAL_OPTIONS.find((o) => o.value === formData.referral)?.label || '';

  const summary = [
    { k: 'Services', v: config.services.filter((s) => services.includes(s.key)).map((s) => s.label).join(', ') },
    { k: 'Contact', v: [formData.fullName, formData.email, formData.phone].map((x) => x.trim()).filter(Boolean).join(' · ') },
    { k: 'Property', v: [formData.address.trim(), formData.postalCode.trim().toUpperCase()].filter(Boolean).join(', ') },
    {
      k: 'Found us via',
      v: formData.referral === 'referral' && formData.referrerName.trim()
        ? `${referralLabel} (${formData.referrerName.trim()})`
        : referralLabel,
    },
  ];

  return (
    <div className="page">
      <Seo />

      <SiteHeader logoTo="/home-owners">
        <PhoneLink />
        <Link to="/home-owners" className="nav-link nav-link--muted nav-link--wide">Close</Link>
      </SiteHeader>

      <main className="wizard">
        {submitted ? (
          <div className="success" role="status">
            <div className="success__icon"><CheckIcon size={30} /></div>
            <h2 className="h2">Quote request sent.</h2>
            <p>
              Thanks, {firstName}. We'll look over the job and email your quote to {formData.email.trim()}. If we have
              questions we'll call {formData.phone.trim()}.
            </p>
            <Link to="/home-owners" className="btn btn--outline-navy">Back to home owners</Link>
          </div>
        ) : (
          <>
            <div className="wizard__head">
              <h2 className="h2">{config.heading}</h2>
              <p className="wizard__question" ref={questionRef} tabIndex={-1}>{STEPS[step].question}</p>
            </div>

            <ol className="progress" aria-label="Progress">
              {STEPS.map((s, i) => {
                const state = i < step ? 'done' : i === step ? 'current' : 'todo';
                return (
                  <li
                    key={s.label}
                    className={`progress__step progress__step--${state}`}
                    aria-current={state === 'current' ? 'step' : undefined}
                  >
                    <span className="progress__dot">{state === 'done' ? <CheckIcon size={14} strokeWidth={3.4} /> : i + 1}</span>
                    <span className="progress__label">{s.label}</span>
                  </li>
                );
              })}
            </ol>

            <form
              className="wizard-card"
              noValidate
              onSubmit={(e) => { e.preventDefault(); goNext(); }}
            >
              {step === 0 && (
                <div>
                  <p className="wizard-card__intro">Select one or more, then press Continue.</p>
                  <div className="option-grid">
                    {config.services.map(({ key, label, Icon }) => (
                      <button
                        key={key}
                        type="button"
                        className="option-tile"
                        aria-pressed={services.includes(key)}
                        onClick={() => toggleService(key)}
                      >
                        <span className="option-tile__check"><CheckIcon size={13} strokeWidth={3.4} /></span>
                        <Icon size={40} />
                        <span className="option-tile__label">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="field-grid">
                  <label className="field field--full">
                    <span className="field__label">Full name</span>
                    <input className="input" type="text" name="fullName" autoComplete="name" placeholder="Jane Smith"
                      value={formData.fullName} onChange={handleInput} />
                  </label>
                  <label className="field">
                    <span className="field__label">Email</span>
                    <input className="input" type="email" name="email" autoComplete="email" placeholder="jane@example.com"
                      value={formData.email} onChange={handleInput} />
                  </label>
                  <label className="field">
                    <span className="field__label">Phone number</span>
                    <input className="input" type="tel" name="phone" autoComplete="tel" placeholder="(416) 555-0123"
                      value={formData.phone} onChange={handleInput} />
                  </label>
                </div>
              )}

              {step === 2 && (
                <div className="field-grid">
                  <label className="field field--full">
                    <span className="field__label">Property address</span>
                    <input className="input" type="text" name="address" autoComplete="street-address" placeholder="123 Maple Ave, Toronto"
                      value={formData.address} onChange={handleInput} />
                  </label>
                  <label className="field">
                    <span className="field__label">Postal code</span>
                    <input className="input" type="text" name="postalCode" autoComplete="postal-code" placeholder="M4B 1B3"
                      value={formData.postalCode} onChange={handleInput} />
                  </label>
                </div>
              )}

              {step === 3 && (
                <div>
                  <RadioGroup
                    name="referral"
                    legend="How did you find out about us?"
                    hideLegend
                    options={REFERRAL_OPTIONS}
                    value={formData.referral}
                    onChange={(v) => setField('referral', v)}
                  />
                  {formData.referral === 'referral' && (
                    <label className="field reveal-field">
                      <span className="field__label">Who referred you?</span>
                      <input className="input" type="text" name="referrerName" autoComplete="off"
                        value={formData.referrerName} onChange={handleInput} />
                    </label>
                  )}
                </div>
              )}

              {step === 4 && (
                <div>
                  {seasonal && (
                    <RadioGroup name="frequency" legend="How often should we come?" pair
                      options={FREQUENCY_OPTIONS} value={formData.frequency} onChange={(v) => setField('frequency', v)} />
                  )}
                  {wantsMowing && (
                    <>
                      <RadioGroup name="lawnArea" legend="Do you want both front and back lawns cut?" pair
                        options={LAWN_AREA_OPTIONS} value={formData.lawnArea} onChange={(v) => setField('lawnArea', v)} />
                      <RadioGroup name="bagClippings" legend="Should the student bag the grass clippings?" pair
                        options={YES_NO} value={formData.bagClippings} onChange={(v) => setField('bagClippings', v)} />
                      {needsBagAnswer && (
                        <RadioGroup name="provideBags" legend="Will you be providing the bags?" pair
                          options={YES_NO} value={formData.provideBags} onChange={(v) => setField('provideBags', v)} />
                      )}
                    </>
                  )}

                  <div className="stack">
                    <label className="field">
                      <span className="field__label">Additional comments <span className="field__optional">(optional)</span></span>
                      <textarea className="input" name="message" rows={5}
                        placeholder="Gate code, size of the yard, best days to come by..."
                        value={formData.message} onChange={handleInput} />
                    </label>

                    <label className="field">
                      <span className="field__label">
                        Photo of the yard <span className="field__optional">(optional)</span>
                      </span>
                      <input className="input input--file" type="file" name="photo" accept="image/*" onChange={handlePhoto} />
                      {photoError
                        ? <p className="field__error" role="alert">{photoError}</p>
                        : <p className="field__help">{photoBusy ? 'Preparing photo...' : formData.photo ? formData.photo.name : 'A photo of the area helps us quote.'}</p>}
                    </label>

                    <dl className="summary">
                      {summary.map((row) => (
                        <div key={row.k} className="summary__row">
                          <dt>{row.k}</dt>
                          <dd>{row.v}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>

                  <div className="turnstile-wrap">
                    <Turnstile key={turnstileKey} sitekey={TURNSTILE_SITE_KEY} onVerify={handleTurnstile} />
                  </div>
                </div>
              )}

              <div className="wizard-actions">
                <button
                  type="button"
                  className="btn btn--outline wizard-actions__back"
                  onClick={goBack}
                  style={{ visibility: step === 0 ? 'hidden' : 'visible' }}
                >
                  Back
                </button>
                <div className="wizard-actions__next">
                  {!stepValid && <span className="wizard-actions__hint">{hints[step]}</span>}
                  <button type="submit" className="btn btn--primary btn--md" aria-disabled={!stepValid || loading || photoBusy}>
                    {loading ? 'Sending...' : isLastStep ? 'Request my quote' : 'Continue'}
                    <ArrowIcon size={17} />
                  </button>
                </div>
              </div>
              {responseMessage && <p className="form-message" role="alert">{responseMessage}</p>}
            </form>
          </>
        )}
      </main>
    </div>
  );
}

function RadioGroup({ name, legend, hideLegend = false, pair = false, options, value, onChange }) {
  return (
    <fieldset className="fieldset">
      <legend className={hideLegend ? 'visually-hidden' : undefined}>{legend}</legend>
      <div className={`radio-grid${pair ? ' radio-grid--pair' : ''}`}>
        {options.map((o) => (
          <label key={o.value} className={`radio-card${value === o.value ? ' radio-card--checked' : ''}`}>
            <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
            <span className="radio-card__ring" />
            {o.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
